import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { SelectCombobox } from '../SelectCombobox'

import {
  loadInventoryConnection,
  loadInventorySnapshot,
  loadInventoryStores,
  loadInventorySyncRuns,
  saveInventoryConnection,
  syncInventory,
  updateInventoryStoreRole,
  type InventoryConnectionDto,
  type InventorySnapshotDto,
  type InventoryStoreDto,
  type InventoryStoreRole,
  type InventorySyncRunDto,
} from '../../api/inventoryConnection'
import { formatApiErrorMessage } from '../../api/apiErrorMessage'
import { ApiError } from '../../api/client'
import { listRestaurantPrivateSuppliers } from '../../api/restaurantMyPrices'
import { useAuth } from '../../auth/AuthProvider'
import { ImportStepper } from '../ImportStepper'
import { useActiveVenue } from '../../venues/ActiveVenueProvider'
import { isDemoStandEnabled } from '../../dev/demoStand'
import { inventoryDemoCatalog } from '../../dev/inventory-demo-catalog'

const roleLabels: Record<InventoryStoreRole, string> = {
  KITCHEN: 'Кухня',
  BAR: 'Бар',
  IGNORE: 'Не учитывать',
}

const syncStatusLabels: Record<InventorySyncRunDto['status'], string> = {
  RUNNING: 'Выполняется',
  OK: 'Успешно',
  FAILED: 'Ошибка',
}

const wizardSteps = ['Доступ', 'Синхронизация', 'Склады', 'Прайсы', 'Готово']

export const InventoryConnectionSettings = () => {
  const { session, organizations } = useAuth()
  const { activeVenueId, activeVenueName } = useActiveVenue()
  const organizationId = session?.organizationId ?? null
  const isPublicDemo = isDemoStandEnabled()
  const canEdit =
    isPublicDemo || organizations.find((org) => org.id === organizationId)?.can_edit === true
  const [connection, setConnection] = useState<InventoryConnectionDto | null>(null)
  const [stores, setStores] = useState<InventoryStoreDto[]>([])
  const [snapshot, setSnapshot] = useState<InventorySnapshotDto | null>(null)
  const [syncRuns, setSyncRuns] = useState<InventorySyncRunDto[]>([])
  const [host, setHost] = useState(isPublicDemo ? 'https://demo.example.invalid' : '')
  const [login, setLogin] = useState(isPublicDemo ? 'demo-api' : '')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [suppliersCount, setSuppliersCount] = useState<number | null>(null)
  const [rolesConfirmed, setRolesConfirmed] = useState(false)
  const [justSynced, setJustSynced] = useState(false)
  const [stepOverride, setStepOverride] = useState<number | null>(null)
  const openedAtFirstStep = useRef(false)

  const reloadSuppliers = useCallback(async () => {
    if (organizationId == null || activeVenueId == null) {
      setSuppliersCount(null)
      return
    }
    if (isPublicDemo) {
      setSuppliersCount(new Set(inventoryDemoCatalog.map((offer) => offer.supplierId)).size)
      return
    }
    try {
      const items = await listRestaurantPrivateSuppliers(organizationId, activeVenueId)
      setSuppliersCount(items.length)
    } catch {
      setSuppliersCount(0)
    }
  }, [activeVenueId, organizationId, isPublicDemo])

  useEffect(() => {
    setRolesConfirmed(false)
    setJustSynced(false)
    setStepOverride(null)
    void reloadSuppliers()
  }, [reloadSuppliers])

  useEffect(() => {
    if (organizationId == null || activeVenueId == null) {
      setConnection(null)
      setStores([])
      setSnapshot(null)
      setSyncRuns([])
      return
    }
    let cancelled = false
    setConnection(null)
    setStores([])
    setSnapshot(null)
    setSyncRuns([])
    setLoading(true)
    setError(null)
    void loadInventoryConnection(organizationId, activeVenueId)
      .then(async (loaded) => {
        if (cancelled) return
        setConnection(loaded)
        const [loadedStores, loadedSnapshot, loadedSyncRuns] = await Promise.all([
          loadInventoryStores(organizationId, activeVenueId),
          loadInventorySnapshot(organizationId, activeVenueId),
          loadInventorySyncRuns(organizationId, activeVenueId),
        ])
        if (!cancelled) {
          setStores(loadedStores)
          setSnapshot(loadedSnapshot)
          setSyncRuns(loadedSyncRuns)
        }
      })
      .catch((loadError) => {
        if (cancelled) return
        if (loadError instanceof ApiError && loadError.status === 404) {
          setConnection(null)
          setStores([])
          setSnapshot(null)
          setSyncRuns([])
          return
        }
        setError(formatApiErrorMessage(loadError, 'Не удалось загрузить подключение iiko'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeVenueId, organizationId])

  const save = async (provider: 'FAKE' | 'IIKO', secret: Record<string, string> | null) => {
    if (organizationId == null || activeVenueId == null) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const saved = await saveInventoryConnection(organizationId, activeVenueId, provider, secret)
      setConnection(saved)
      setStores([])
      setSnapshot(null)
      setSyncRuns([])
      setPassword('')
      setStepOverride(null)
      setMessage(
        isPublicDemo || provider === 'FAKE'
          ? 'Демо-источник подключён'
          : 'Подключение iiko сохранено',
      )
    } catch (saveError) {
      setError(formatApiErrorMessage(saveError, 'Не удалось сохранить подключение'))
    } finally {
      setSaving(false)
    }
  }

  const handleIikoSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (isPublicDemo) {
      await save('IIKO', null)
      return
    }
    const values = [host.trim(), login.trim(), password]
    const secret = values.every((value) => value === '')
      ? null
      : values.every((value) => value !== '')
        ? { host: values[0], login: values[1], password: values[2] }
        : undefined
    if (secret === undefined) {
      setError('Чтобы изменить доступ, заполните адрес сервера, логин и пароль вместе.')
      return
    }
    if (secret === null && connection?.provider !== 'IIKO') {
      setError('Заполните адрес сервера, логин и пароль iiko.')
      return
    }
    await save('IIKO', secret)
  }

  const handleSync = async () => {
    if (organizationId == null || activeVenueId == null) return
    setSyncing(true)
    setError(null)
    setMessage(null)
    try {
      const synced = await syncInventory(organizationId, activeVenueId)
      setSnapshot(synced)
      setJustSynced(true)
      setStepOverride(null)
      const [loadedStores, loadedSyncRuns] = await Promise.all([
        loadInventoryStores(organizationId, activeVenueId),
        loadInventorySyncRuns(organizationId, activeVenueId),
      ])
      setStores(loadedStores)
      setSyncRuns(loadedSyncRuns)
      setMessage(synced.error ? null : `Остатки за ${synced.accounting_date} синхронизированы`)
    } catch (syncError) {
      setError(formatApiErrorMessage(syncError, 'Не удалось синхронизировать остатки'))
    } finally {
      setSyncing(false)
    }
  }

  const handleRole = async (store: InventoryStoreDto, role: InventoryStoreRole) => {
    if (organizationId == null || activeVenueId == null) return
    try {
      const updated = await updateInventoryStoreRole(organizationId, activeVenueId, store.id, role)
      setStores((current) => current.map((item) => (item.id === updated.id ? updated : item)))
    } catch (roleError) {
      setError(formatApiErrorMessage(roleError, 'Не удалось изменить роль склада'))
    }
  }

  const syncOk = connection != null && snapshot?.synced_at != null && !snapshot.error
  const storesOk =
    stores.some((store) => store.role !== 'IGNORE') && (rolesConfirmed || !justSynced)
  const pricesOk = (suppliersCount ?? 0) > 0
  const derivedStep = connection == null ? 0 : !syncOk ? 1 : !storesOk ? 2 : !pricesOk ? 3 : 4
  const step = stepOverride != null ? Math.min(stepOverride, derivedStep) : derivedStep
  const complete = derivedStep === 4 && stepOverride == null

  /**
   * Незаконченный мастер открывается с первого шага.
   *
   * Иначе человек попадал сразу на «Синхронизацию» и не видел, какой адрес и логин уже сохранены.
   * Пройденные шаги при этом не блокируются: «Далее» ограничено derivedStep.
   */
  useEffect(() => {
    // suppliersCount приезжает отдельным запросом: до него derivedStep ещё не окончательный
    // и настроенное подключение выглядело бы незаконченным.
    if (loading || suppliersCount == null || openedAtFirstStep.current) return
    openedAtFirstStep.current = true
    if (derivedStep > 0 && derivedStep < 4) {
      setStepOverride(0)
    }
  }, [derivedStep, loading, suppliersCount])

  const backButton = (target: number) => (
    <button
      type="button"
      className="echat-btn echat-btn--ghost echat-btn--sm"
      onClick={() => setStepOverride(target)}
    >
      Назад
    </button>
  )

  const credentialsForm =
    activeVenueId != null ? (
      <form className="market-form" onSubmit={(event) => void handleIikoSubmit(event)}>
        {connection ? (
          <p className="echat-muted">
            {connection.demo ? 'Сейчас подключены демо-данные.' : 'iiko подключён.'}
          </p>
        ) : null}
        {isPublicDemo ? (
          <p className="echat-muted">
            Демонстрационное подключение использует подготовленные данные. Поля ниже показывают
            пример настройки; настоящий доступ к iiko здесь не требуется.
          </p>
        ) : (
          <p className="echat-muted">
            Нужен сервер iiko, а не iikoCloud: остатки по складам отдаёт только он. Пользователю —
            доступ к API и отчётам по складам. Пароль после сохранения не показывается.{' '}
            <Link to="/help/iiko">Как подключить iiko к Провизиум</Link>
          </p>
        )}
        <label className="echat-field">
          <span className="echat-field__label">Адрес сервера iiko</span>
          <input
            className="echat-field__input"
            type="url"
            placeholder="https://example.iiko.it"
            value={host}
            readOnly={isPublicDemo}
            disabled={!canEdit}
            onChange={(event) => setHost(event.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Логин API</span>
          <input
            className="echat-field__input"
            autoComplete="username"
            value={login}
            readOnly={isPublicDemo}
            disabled={!canEdit}
            onChange={(event) => setLogin(event.target.value)}
          />
        </label>
        <label className="echat-field">
          <span className="echat-field__label">Пароль API</span>
          <input
            className="echat-field__input"
            type="password"
            autoComplete="new-password"
            value={password}
            readOnly={isPublicDemo}
            placeholder={isPublicDemo ? 'Демонстрационный доступ' : undefined}
            disabled={!canEdit}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <div className="market-actions-row">
          <button
            type="submit"
            className="echat-btn echat-btn--primary echat-btn--sm"
            disabled={!canEdit || saving}
          >
            {saving
              ? 'Сохранение…'
              : isPublicDemo
                ? 'Подключить демо iiko'
                : connection?.provider === 'IIKO'
                  ? 'Обновить iiko'
                  : 'Подключить iiko'}
          </button>
          {connection == null ? (
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              disabled={!canEdit || saving}
              onClick={() => void save('FAKE', {})}
            >
              Попробовать на демо-данных
            </button>
          ) : (
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => setStepOverride(1)}
            >
              Далее
            </button>
          )}
        </div>
      </form>
    ) : null

  const syncButton = (label: string) => (
    <button
      type="button"
      className="echat-btn echat-btn--primary echat-btn--sm"
      disabled={!canEdit || syncing}
      onClick={() => void handleSync()}
    >
      {syncing ? 'Синхронизация…' : label}
    </button>
  )

  const snapshotInfo =
    connection && snapshot ? (
      <div>
        <p className="echat-muted">
          {snapshot.synced_at
            ? `Последний запуск: ${new Date(snapshot.synced_at).toLocaleString('ru-RU')}. Учётная дата: ${snapshot.accounting_date}.`
            : 'Синхронизация ещё не запускалась.'}
        </p>
      </div>
    ) : null

  const storesTable =
    stores.length > 0 ? (
      <div className="market-table-wrap">
        <table className="market-table">
          <thead>
            <tr>
              <th>Склад</th>
              <th>Роль</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((store) => (
              <tr key={store.id}>
                <td>{store.name}</td>
                <td>
                  <SelectCombobox
                    className="market-store-role"
                    label={`Роль склада «${store.name}»`}
                    hideLabel
                    listAllOnFocus
                    value={store.role}
                    disabled={!canEdit}
                    options={Object.entries(roleLabels).map(([value, label]) => ({ value, label }))}
                    onChange={(next) => void handleRole(store, next as InventoryStoreRole)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null

  const historyTable =
    syncRuns.length > 0 ? (
      <div className="market-table-wrap">
        <table className="market-table">
          <caption>История синхронизаций</caption>
          <thead>
            <tr>
              <th>Запуск</th>
              <th>Учётная дата</th>
              <th>Статус</th>
              <th>Детали</th>
            </tr>
          </thead>
          <tbody>
            {syncRuns.map((run) => (
              <tr key={run.id}>
                <td>{new Date(run.started_at).toLocaleString('ru-RU')}</td>
                <td>{run.accounting_date}</td>
                <td>{syncStatusLabels[run.status]}</td>
                <td>{run.error ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null

  const wizardBody = (
    <>
      {step === 0 ? credentialsForm : null}
      {step === 1 ? (
        <>
          <p>
            {isPublicDemo
              ? 'Загрузим подготовленные склады и остатки демо.'
              : 'Загрузим склады и остатки из iiko. Обычно это занимает несколько секунд.'}
          </p>
          {snapshotInfo}
          <div className="market-actions-row">
            {backButton(0)}
            {syncButton(
              snapshot?.error ? 'Повторить синхронизацию' : 'Запустить первую синхронизацию',
            )}
          </div>
        </>
      ) : null}
      {step === 2 ? (
        <>
          <p>
            Назначьте складам роли: кухня или бар. Склады с ролью «Не учитывать» не попадут в
            расчёт.
          </p>
          {storesTable}
          <div className="market-actions-row">
            {backButton(1)}
            <button
              type="button"
              className="echat-btn echat-btn--primary echat-btn--sm"
              disabled={!stores.some((store) => store.role !== 'IGNORE')}
              onClick={() => {
                setRolesConfirmed(true)
                setStepOverride(null)
              }}
            >
              Далее
            </button>
          </div>
        </>
      ) : null}
      {step === 3 ? (
        <>
          <p>
            Чтобы подобрать цену для заказа, нужны прайсы поставщиков. Пока в «Моих прайсах» нет ни
            одного поставщика.
          </p>
          <div className="market-actions-row">
            {backButton(2)}
            <Link className="echat-btn echat-btn--primary echat-btn--sm" to="/my-prices/import">
              Загрузить прайс
            </Link>
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => void reloadSuppliers()}
            >
              Проверить снова
            </button>
          </div>
        </>
      ) : null}
    </>
  )

  return (
    <article id="inventory-integration" className="market-settings-column">
      <header className="market-settings-column__head">
        <h2 className="market-settings-column__title">Интеграция с iiko</h2>
        <p className="market-settings-column__desc">
          {activeVenueName ? `Заведение «${activeVenueName}».` : 'Выберите заведение.'}
        </p>
      </header>
      <div className="market-settings-column__body">
        {loading ? <p className="echat-muted">Загрузка подключения…</p> : null}
        {message ? <p className="market-success">{message}</p> : null}
        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}

        {activeVenueId != null && !loading ? (
          complete ? (
            <>
              <p className="market-success">
                {connection?.demo ? 'Демо-источник подключён' : 'iiko подключён'}
                {snapshot?.synced_at
                  ? `, синхронизировано ${new Date(snapshot.synced_at).toLocaleString('ru-RU')}`
                  : ''}
                .
              </p>
              <div className="market-actions-row">
                <Link className="echat-btn echat-btn--primary echat-btn--sm" to="/purchase">
                  Открыть «Заказ по остаткам»
                </Link>
                {syncButton('Синхронизировать сейчас')}
              </div>
              <details>
                <summary>Параметры подключения</summary>
                {credentialsForm}
                {snapshotInfo}
                {storesTable}
                {historyTable}
              </details>
            </>
          ) : (
            <>
              <ImportStepper steps={wizardSteps} current={step} label="Шаги подключения iiko" />
              {snapshot?.error ? (
                <p className="echat-error" role="alert">
                  Последняя синхронизация завершилась ошибкой: {snapshot.error}
                </p>
              ) : null}
              {wizardBody}
              {historyTable}
            </>
          )
        ) : null}
      </div>
    </article>
  )
}
